import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { fetcher, kratosPublicFetcher, publicFetcher, staticOptions } from "lib/core/apiClient";
import { useAuthContext } from "lib/contexts/AuthProvider";
import UpSellProductCard from "components/upsell/cards/UpSellProductCard";
import Carousel from "components/common/carousel/Carousel";
import * as Tab from "components/common/Tabs";
import { classNames } from "lib/utils/helpers";
import { parseUpSellData, parseUpsellIds, parseUpsellWithProduct } from "lib/utils/upsell";
import {
  UpsellPositionType,
  UpSellProductType,
  UpsellAndWishlistGroupsOnDiscountType,
} from "lib/types/upsell";
import UpSellVariantSelectorDialog from "components/upsell/dialogs/UpSellVariantsSelectorDialog";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import { useWishlistContext } from "lib/contexts/WishlistProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import WishListItemCard from "components/wishlist/WishlistItemCard";
import { WishlistItemType } from "lib/types/wishlist";
import { AnimateLoading } from "components/animation/AnimateLoading";
import UpsellSkeleton from "components/common/skeleton/UpsellSkeleton";
import { useMerchantUiConfig } from "lib/contexts/MerchantUIConfigProvider";

interface CustomTabConfig {
  value: string;
  title: string;
  products: UpSellProductType[];
}
interface UpsellSectionProps {
  parent?: UpsellPositionType;
  customTabs?: CustomTabConfig[];
  onUpsellDataParsed?: (data: {
    discountedUpsells: UpSellProductType[];
    nonDiscountedUpsells: UpSellProductType[];
  }) => void;
}

const UpSellAndWishlistSection: React.FC<UpsellSectionProps> = React.memo(
  ({ parent, customTabs, onUpsellDataParsed }) => {
    const [searchParams] = useSearchParams();
    const isBackgroundLoad = searchParams.get("is_background_load") === "true";
    const { t } = useLocale();
    const {
      state: { isAuthenticated },
    } = useAuthContext();
    const {
      state: { checkoutId, checkoutUIMetadata, wishlistConfig, checkoutItems },
    } = useCheckoutContext();
    const {
      state: {
        cartMetadata: { ui_attributes },
      },
    } = useMerchantUiConfig();

    const { sendAnalyticsEvent } = useSendAnalyticsEvent();
    const {
      state: { count: wishlistedItemsCount, wishlistedItems },
    } = useWishlistContext();

    const { upsell } = ui_attributes || {};

    const [isVariantSelectorOpen, setIsVariantSelector] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<UpSellProductType>();

    const getUpsellUrl = (): string | null => {
      if (!checkoutId) {
        const tokenId = searchParams.get("tokenId");
        if (tokenId) return `/upsell?tokenId=${tokenId}`;
      }

      const baseUrl = Boolean(isAuthenticated) ? "/checkout/v1/checkout" : "/v1/checkout";
      return `${baseUrl}/${checkoutId}/upsell`;
    };

    const getFetcher = () => {
      if (checkoutId) return Boolean(isAuthenticated) ? fetcher : publicFetcher;
      return kratosPublicFetcher;
    };

    const { data: upsellResponse, isValidating } = useSWR(() => getUpsellUrl(), getFetcher(), staticOptions);

    // Useffect for passing data to parent
    useEffect(() => {
      if (!isValidating) {
        if (!Boolean(upsellResponse)) {
          onUpsellDataParsed?.({
            discountedUpsells: [],
            nonDiscountedUpsells: [],
          });
        } else {
          const parsedData = parseUpSellData(upsellResponse);
          onUpsellDataParsed?.(parsedData);
        }
      }
    }, [upsellResponse, isValidating, onUpsellDataParsed]);

    const { discountedUpsells, nonDiscountedUpsells } = useMemo(() => {
      if (!Boolean(upsellResponse)) {
        const emptyData = {
          discountedUpsells: [],
          nonDiscountedUpsells: [],
        };
        return emptyData;
      }
      return parseUpSellData(upsellResponse);
    }, [upsellResponse]);

    const isWishlistEnabled = useMemo(() => {
      return !Boolean(wishlistConfig?.isEnabled)
        ? false
        : Boolean(
            Boolean(parent === "CART" && !Boolean(wishlistConfig?.config?.cart?.enabled)) ||
              Boolean(parent === "PAYMENT_PAGE" && !Boolean(wishlistConfig?.config?.checkout?.enabled)),
          )
        ? false
        : true;
    }, [parent, wishlistConfig]);

    const filteredWishlistedProducts = useMemo(() => {
      const checkoutItemsVariantIds = checkoutItems?.map((item) => item.variant_id);
      return wishlistedItems?.filter((item) => !checkoutItemsVariantIds.some((id) => id === item.variantId));
    }, [checkoutItems, wishlistedItems]);

    useEffect(() => {
      const loaderGroupIds = parseUpsellIds(upsellResponse);
      if (isBackgroundLoad) return;
      if (!Boolean(loaderGroupIds.length)) return;
      sendAnalyticsEvent({
        eventName: analyticsEvents.UPSELL_WIDGET_LOADED,
        eventType: "load",
        eventFor: [analyticsTypes.SF_ANALYTICS],
        context: parent === "CART" ? "cart" : "checkout",
        metaData: {
          upsellData: {
            position: parent === "CART" ? "cart" : "payment_page",
            groupData: parseUpsellWithProduct(upsellResponse),
          },
        },
      });
    }, [upsellResponse]);

    const openVariantSelector = (selectedProduct: UpSellProductType) => {
      setSelectedProduct(selectedProduct);
      setIsVariantSelector(true);
    };

    const closeVariantSelector = () => {
      setIsVariantSelector(false);
    };

    const getTabConfig = () => {
      let tabs = [];
      const isCart = ["EMPTY_CART", "CART"].includes(parent ?? "");
      const discountedTitle = isCart
        ? upsell?.discounted_upsell_title
        : checkoutUIMetadata?.upsellConfig?.discountedTitle;

      const nonDiscountedTitle = isCart ? upsell?.upsell_title : checkoutUIMetadata?.upsellConfig?.title;

      if (discountedUpsells.length) {
        tabs.push({
          value: UpsellAndWishlistGroupsOnDiscountType.DISCOUNTED,
          title: discountedTitle || t("best_offers"),
          content: (
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full flex-col items-start justify-start space-y-2">
                <Carousel count={discountedUpsells?.length} customCSS="pr-3" shouldRenderDots={false}>
                  {discountedUpsells?.map((upSellProduct: UpSellProductType, index: number) => (
                    <UpSellProductCard
                      isDiscounted={!checkoutUIMetadata?.upsellConfig?.shouldOverrideDiscountedUpsell}
                      product={upSellProduct}
                      openVariantSelector={openVariantSelector}
                      key={`upsell-product${index + 1}`}
                      position={parent ? parent : "PAYMENT_PAGE"}
                    />
                  ))}
                </Carousel>
              </div>
            </div>
          ),
        });
      }

      if (nonDiscountedUpsells.length) {
        tabs.push({
          value: UpsellAndWishlistGroupsOnDiscountType.NON_DISCOUNTED,
          title: nonDiscountedTitle || t("you_might_also_like"),
          content: (
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full flex-col items-start justify-start space-y-2">
                <Carousel count={nonDiscountedUpsells?.length} customCSS="pr-3" shouldRenderDots={false}>
                  {nonDiscountedUpsells?.map((upSellProduct: UpSellProductType, index: number) => (
                    <UpSellProductCard
                      isDiscounted={false}
                      product={upSellProduct}
                      openVariantSelector={openVariantSelector}
                      key={`upsell-product${index + 1}`}
                      position={parent ? parent : "PAYMENT_PAGE"}
                    />
                  ))}
                </Carousel>
              </div>
            </div>
          ),
        });
      }

      if (
        isWishlistEnabled &&
        parent === "CART" &&
        wishlistedItemsCount > 0 &&
        Boolean(filteredWishlistedProducts?.length)
      ) {
        tabs.push({
          value: UpsellAndWishlistGroupsOnDiscountType.WISHLIST,
          title:
            parent === "CART"
              ? wishlistConfig?.config.cart.header || t("your_wishlist")
              : wishlistConfig?.config.checkout.header || t("your_wishlist"),
          content: (
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full flex-col items-start justify-start space-y-2">
                <Carousel
                  count={filteredWishlistedProducts?.length}
                  customCSS="pr-3"
                  shouldRenderDots={false}>
                  {filteredWishlistedProducts?.map((wishlistItem: WishlistItemType, index: number) => (
                    <WishListItemCard
                      key={`item-${index + 1}`}
                      index={index}
                      item={wishlistItem}
                      parent="UPSELL_STRIP"
                    />
                  ))}
                </Carousel>
              </div>
            </div>
          ),
        });
      }

      if (customTabs?.length) {
        customTabs.forEach((customTab) => {
          tabs.push({
            value: customTab.value,
            title: customTab.title,
            content: (
              <div className="flex w-full flex-row items-center">
                <div className="flex w-full flex-col items-start justify-start space-y-2">
                  <Carousel count={customTab.products?.length} customCSS="pr-3" shouldRenderDots={false}>
                    {customTab.products?.map((upSellProduct: UpSellProductType, index: number) => (
                      <UpSellProductCard
                        isDiscounted={false}
                        product={upSellProduct}
                        openVariantSelector={openVariantSelector}
                        key={`upsell-product${index + 1}`}
                        position={parent ? parent : "PAYMENT_PAGE"}
                      />
                    ))}
                  </Carousel>
                </div>
              </div>
            ),
          });
        });
      }

      if (checkoutUIMetadata?.upsellConfig?.shouldOverrideDiscountedUpsell) {
        tabs = tabs.reverse();
      }

      return tabs;
    };

    if (!isValidating && !getTabConfig().length) return null;

    return (
      <div className="mt-3">
        <AnimateLoading loading={isValidating}>
          <AnimateLoading.Skeleton>
            <UpsellSkeleton />
          </AnimateLoading.Skeleton>
          <AnimateLoading.Content>
            <div className="flex flex-col">
              <div className="flex w-full flex-col gap-3 rounded-2xl border-[2px] border-gray-light bg-white p-3 pb-0 pr-0">
                <Tab.Root
                  defaultValue={getTabConfig()[0]?.value || UpsellAndWishlistGroupsOnDiscountType.DISCOUNTED}>
                  <Tab.List className="spacex-2 mb-2 flex items-start space-x-4 text-sm">
                    {getTabConfig().map((tab) => (
                      <Tab.Trigger
                        key={tab.value}
                        value={tab.value}
                        renderTrigger={({ isSelected, onClick }) => (
                          <button
                            onClick={onClick}
                            className={classNames(
                              "relative",
                              isSelected ? "font-semibold text-yay-dark" : "font-medium text-coal-light",
                            )}>
                            {tab.title}
                            {Boolean(isSelected) && (
                              <span className="absolute -bottom-2 left-0 w-full border border-coal-dark"></span>
                            )}
                          </button>
                        )}
                      />
                    ))}
                  </Tab.List>
                  {getTabConfig().map((tab) => (
                    <Tab.Content key={tab.value} value={tab.value}>
                      {tab.content}
                    </Tab.Content>
                  ))}
                </Tab.Root>
              </div>
              <UpSellVariantSelectorDialog
                upsellProduct={selectedProduct}
                isVariantSelectorOpen={isVariantSelectorOpen}
                closeVariantSelector={closeVariantSelector}
                position={parent ? parent : "PAYMENT_PAGE"}
              />
            </div>
          </AnimateLoading.Content>
        </AnimateLoading>
      </div>
    );
  },
);

export default UpSellAndWishlistSection;
